import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

interface AuthContextType {
  currentUser: User | null;
  userDetails: UserDetails | null;
  loading: boolean;
  register: (email: string, password: string, userData: UserRegistrationData) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAdmin: boolean;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
}

interface UserDetails {
  firstName: string;
  lastName: string;
  province: string;
  teamId: string | null;
  isAdmin: boolean;
  createdAt: string;
  emailVerified?: boolean;
  emailNotifications?: boolean;
  theme?: 'light' | 'dark';
  email?: string;
  totalRaised?: number;
  donationCount?: number;
}

export interface UserRegistrationData {
  firstName: string;
  lastName: string;
  province: string;
  teamId: string | null;
  teamName?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data() as UserDetails;
            setUserDetails(userData);
            setIsAdmin(userData.isAdmin || false);
            
            // Update emailVerified status in Firestore if it changed
            if (userData.emailVerified !== user.emailVerified || userData.email !== user.email) {
              await updateDoc(doc(db, 'users', user.uid), {
                emailVerified: user.emailVerified,
                email: user.email
              });
            }
          }
        } catch (error) {
          console.error("Error fetching user details:", error);
        }
      } else {
        setUserDetails(null);
        setIsAdmin(false);
      }
      
      setLoading(false);
    });
    
    return unsubscribe;
  }, []);

  // Send password reset email
  async function sendPasswordResetEmail(email: string) {
    return firebaseSendPasswordResetEmail(auth, email);
  }

  // Resend verification email
  async function resendVerificationEmail() {
    if (currentUser) {
      return sendEmailVerification(currentUser);
    }
    throw new Error('No user is signed in');
  }

  // Register new user
  async function register(email: string, password: string, userData: UserRegistrationData) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update display name
      await updateProfile(user, {
        displayName: `${userData.firstName} ${userData.lastName}`
      });
      
      // Create user document in Firestore
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, {
        firstName: userData.firstName,
        lastName: userData.lastName,
        province: userData.province,
        teamId: userData.teamId,
        isAdmin: false,
        createdAt: new Date().toISOString(),
        emailVerified: false,
        emailNotifications: true,
        email: email
      });
      
      // Send verification email
      await sendEmailVerification(user);
      
      // Ensure province document exists
      const provinceRef = doc(db, 'provinces', userData.province);
      const provinceDoc = await getDoc(provinceRef);
      
      if (!provinceDoc.exists()) {
        // Create province document if it doesn't exist
        await setDoc(provinceRef, {
          totalMiles: 0,
          participantCount: 1
        });
      } else {
        // Update participant count if province exists
        const provinceData = provinceDoc.data();
        await setDoc(provinceRef, {
          ...provinceData,
          participantCount: (provinceData.participantCount || 0) + 1
        }, { merge: true });
      }
      
      // Create statistics document if it doesn't exist
      const statsRef = doc(db, 'statistics', 'totalMiles');
      const statsDoc = await getDoc(statsRef);
      
      if (!statsDoc.exists()) {
        await setDoc(statsRef, {
          miles: 0
        });
      }
      
      // If creating a new team
      if (userData.teamName && !userData.teamId) {
        const teamDocRef = doc(db, 'teams', user.uid); // Using user ID as team ID
        await setDoc(teamDocRef, {
          name: userData.teamName.toLowerCase(), // Store lowercase for case-insensitive search
          displayName: userData.teamName, // Store original casing for display
          createdBy: user.uid,
          memberCount: 1,
          totalMiles: 0,
          province: userData.province,
          createdAt: new Date().toISOString()
        });
        
        // Update user with team ID
        await setDoc(userDocRef, { teamId: user.uid }, { merge: true });
      }
      
      // If joining existing team, update team member count
      if (userData.teamId) {
        // Logic to update team member count will be handled by a Firestore trigger
      }
      
    } catch (error) {
      console.error("Error in registration:", error);
      throw error;
    }
  }

  // Login user
  async function login(email: string, password: string) {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error("Error in login:", error);
      throw error;
    }
  }

  // Logout user
  async function logout() {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error in logout:", error);
      throw error;
    }
  }

  const value = {
    currentUser,
    userDetails,
    loading,
    register,
    login,
    logout,
    isAdmin,
    sendPasswordResetEmail,
    resendVerificationEmail
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
} 